import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';

export interface EditSocialModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { instagram: string; x: string; facebook: string; youtube: string; website: string; linkedin: string }) => Promise<void>;
  initialData: { instagram: string; x: string; facebook: string; youtube: string; website: string; linkedin: string };
}

export const EditSocialModal: React.FC<EditSocialModalProps> = ({ open, onClose, onSave, initialData }) => {
  const { t } = useI18n();
  const [instagram, setInstagram] = useState(initialData.instagram || '');
  const [x, setX] = useState(initialData.x || '');
  const [facebook, setFacebook] = useState(initialData.facebook || '');
  const [youtube, setYoutube] = useState(initialData.youtube || '');
  const [website, setWebsite] = useState(initialData.website || '');
  const [linkedin, setLinkedin] = useState(initialData.linkedin || '');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setInstagram(initialData.instagram || '');
    setX(initialData.x || '');
    setFacebook(initialData.facebook || '');
    setYoutube(initialData.youtube || '');
    setWebsite(initialData.website || '');
    setLinkedin(initialData.linkedin || '');
  }, [initialData, open]);

  const validateUrl = (value: string) => {
    if (!value) return true;
    try {
      new URL(value.startsWith('http') ? value : 'https://' + value);
      return true;
    } catch {
      return false;
    }
  };

  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (instagram && !validateUrl(instagram)) errs.instagram = t('profile.invalid_url');
    if (x && !validateUrl(x)) errs.x = t('profile.invalid_url');
    if (facebook && !validateUrl(facebook)) errs.facebook = t('profile.invalid_url');
    if (youtube && !validateUrl(youtube)) errs.youtube = t('profile.invalid_url');
    if (website && !validateUrl(website)) errs.website = t('profile.invalid_website_url');
    if (linkedin && !validateUrl(linkedin)) errs.linkedin = t('profile.invalid_website_url');
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    try {
      await onSave({ instagram, x, facebook, youtube, website, linkedin });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_social')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_social_description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.instagram')}</label>
            <Input value={instagram} onChange={e => setInstagram(e.target.value)} className="text-black border-black bg-white" />
            {errors.instagram && <p className="text-xs text-red-600 mt-1">{errors.instagram}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.x_twitter')}</label>
            <Input value={x} onChange={e => setX(e.target.value)} className="text-black border-black bg-white" />
            {errors.x && <p className="text-xs text-red-600 mt-1">{errors.x}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.facebook')}</label>
            <Input value={facebook} onChange={e => setFacebook(e.target.value)} className="text-black border-black bg-white" />
            {errors.facebook && <p className="text-xs text-red-600 mt-1">{errors.facebook}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.youtube')}</label>
            <Input value={youtube} onChange={e => setYoutube(e.target.value)} className="text-black border-black bg-white" />
            {errors.youtube && <p className="text-xs text-red-600 mt-1">{errors.youtube}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.website')}</label>
            <Input value={website} onChange={e => setWebsite(e.target.value)} className="text-black border-black bg-white" />
            {errors.website && <p className="text-xs text-red-600 mt-1">{errors.website}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1 text-black">{t('profile.linkedin')}</label>
            <Input value={linkedin} onChange={e => setLinkedin(e.target.value)} className="text-black border-black bg-white" placeholder={t('profile.linkedin_placeholder')} />
            {errors.linkedin && <p className="text-xs text-red-600 mt-1">{errors.linkedin}</p>}
          </div>
        </div>
        <Button
          className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-3 h-11 rounded-full transition-colors duration-200 disabled:opacity-50 mt-6"
          onClick={handleSave}
          disabled={saving || Object.keys(validate()).length > 0}
        >
          {saving ? t('profile.saving') : t('common.save')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}; 